/**
 * Runtime configuration for the session restore feature.
 */

import { Capacitor } from "@capacitor/core";
import { getFrontendConfig } from "../FrontendConfigApi";

/** Storage key used for the app session snapshot. */
export const APP_SESSION_STORAGE_KEY = "flowride.app-session-snapshot";

/**
 * Maximum age of a snapshot before it is discarded on restore (30 minutes).
 * Bounds the OAuth / hosted-checkout round trip, including slow payment
 * confirmation steps (3-D Secure, TWINT app switch).
 */
export const APP_SESSION_RESTORE_TTL_MS = 30 * 60 * 1000;

/** Maximum JSON size of a snapshot; larger snapshots are rejected to avoid storage quota errors. */
export const APP_SESSION_MAX_SIZE_BYTES = 4_000_000;

/**
 * Storage backend chosen based on the runtime platform.
 * @remarks Native WebViews can lose `sessionStorage` during an OAuth redirect,
 * so `localStorage` is used on iOS/Android.
 */
export const APP_SESSION_STORAGE_BACKEND = Capacitor.isNativePlatform() ? "localStorage" : "sessionStorage";

/**
 * Whether session restore is enabled.
 * @returns `true` when the remote feature flag is enabled or not explicitly disabled.
 * @remarks The backend-copied DTO does not yet expose the flag, so a defensive
 * cast is used until `npm run dto:convert:api` regenerates the frontend types.
 */
export function isSessionRestoreEnabled(): boolean {
    const featureFlags = getFrontendConfig().featureFlags as unknown as Record<string, boolean | undefined>;

    return featureFlags.appSessionRestoreEnabled ?? true;
}
